import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  CustomReportPayload,
  CustomReportResponse,
  CustomReportsListResponse,
  CustomReportsSummaryResponse,
} from './interfaces/customReports.interface'

const BASE = '/v1_private/reports'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class CustomReportsApiService {
  private static _instance: CustomReportsApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): CustomReportsApiService {
    if (!CustomReportsApiService._instance) {
      CustomReportsApiService._instance = new CustomReportsApiService()
    }

    return CustomReportsApiService._instance
  }

  public static refresh(): void {
    CustomReportsApiService._instance = new CustomReportsApiService()
  }

  public static async getList(): Promise<CustomReportsListResponse> {
    const response =
      await CustomReportsApiService.instance.client.get<CustomReportsListResponse>(
        BASE,
        { headers: getHeaders() }
      )

    return response.data
  }

  public static async getSummary(): Promise<CustomReportsSummaryResponse> {
    const response =
      await CustomReportsApiService.instance.client.get<CustomReportsSummaryResponse>(
        `${BASE}/summary`,
        { headers: getHeaders() }
      )

    return response.data
  }

  public static async create(
    payload: CustomReportPayload
  ): Promise<CustomReportResponse> {
    const response =
      await CustomReportsApiService.instance.client.post<CustomReportResponse>(
        BASE,
        payload,
        { headers: getHeaders() }
      )

    return response.data
  }

  public static async retry(reportId: string): Promise<CustomReportResponse> {
    const response =
      await CustomReportsApiService.instance.client.post<CustomReportResponse>(
        `${BASE}/${encodeURIComponent(reportId)}/retry`,
        {},
        { headers: getHeaders() }
      )

    return response.data
  }

  public static async remove(reportId: string): Promise<{ success: boolean }> {
    const response = await CustomReportsApiService.instance.client.delete<{
      success: boolean
    }>(`${BASE}/${encodeURIComponent(reportId)}`, { headers: getHeaders() })

    return response.data
  }

  // Fetched as a blob and saved via an object URL rather than navigating to the
  // URL directly: the endpoint needs the session headers, which a plain
  // window.open or <a href> cannot send.
  public static async download(reportId: string, filename: string) {
    const response = await CustomReportsApiService.instance.client.get(
      `${BASE}/${encodeURIComponent(reportId)}/download`,
      { headers: getHeaders(), responseType: 'blob' }
    )

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

export { CustomReportsApiService }
